<template>
  <div
    class="bg-white overflow-auto padding-five-all margin-top-ten gestor-centralizado-table"
  >
    <b-container fluid>
      <b-row v-if="loadError">
        <b-col cols="12">
          <b-alert show variant="danger" class="mb-3">
            {{ loadError }}
          </b-alert>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12">
          <b-table
            class="table-fixed"
            show-empty
            :empty-text="emptyText"
            small
            striped
            hover
            responsive
            :busy="loading"
            :items="items"
            :fields="tableFields"
            :tbody-tr-class="rowClass"
            primary-key="id"
          >
            <template #table-busy>
              <div class="text-center text-danger my-3">
                <b-spinner class="align-middle mr-2" />
                <strong>{{ loadingText }}</strong>
              </div>
            </template>

            <template #cell(nroOs)="{ item }">
              <span :class="{ 'font-italic text-muted': item.esAdicional }">
                {{ item.nroOs }}
              </span>
              <b-badge v-if="item.esManual" variant="warning" class="ml-1"
                >Manual</b-badge
              >
            </template>
            
            <template #cell(costoTotalSinIgv)="{ item }">
              <b-form-input
                v-if="editable && !item.esAdicional && canEditCosto(item)"
                v-model.number="item.costoSinIgv"
                type="number"
                step="0.01"
                min="0"
                class="grey-input text-right"
                @input="$emit('cost-change', item)"
              />
              <span v-else>{{ formatMoney(item.costoTotalSinIgv) }}</span>
            </template>

                        <template #cell(cantidad)="{ item, value }">
              <b-form-input
                v-if="editable && item.esAdicional && canEditCantidad"
                v-model.number="item.cantidad"
                type="number"
                min="0"
                step="1"
                style="min-width: 70px;"
                class="grey-input text-right"
                @keydown="$emit('integer-only', $event)"
                @input="$emit('detalle-change', item)"
                @blur="$emit('cantidad-blur', item)"
              />
              <b-form-input
                v-else-if="editable && !item.esAdicional && canEditCantidad"
                v-model.number="item.cantidad"
                type="number"
                min="0"
                step="1"
                style="min-width: 70px;"
                class="grey-input text-right"
                @keydown="$emit('integer-only', $event)"
                @input="$emit('cantidad-change', item)"
                @blur="$emit('cantidad-blur', item)"
              />
              <span v-else>{{ value }}</span>
            </template>

            <template #cell(penalidad)="{ item }">
              <div
                v-if="!item.esAdicional"
                class="d-flex align-items-center justify-content-end gc-penalidad-cell"
              >
                <span
                  v-if="showPenalidadValue(item)"
                  v-b-tooltip.hover
                  :title="penalidadTooltip(item)"
                  :class="penalidadTooltipClass(item)"
                  class="text-danger font-weight-bold mr-2"
                >
                  {{ formatPenalidadDisplay(item) }}
                </span>
                <span v-else class="text-muted mr-2">—</span>
                <b-button
                  v-if="editable && canEditPenalidades"
                  size="sm"
                  variant="outline-danger"
                  :disabled="Number(item.cantidad) === 0"
                  v-b-tooltip.hover
                  :title="
                    Number(item.cantidad) === 0
                      ? 'No se pueden agregar penalidades a una OS con cantidad 0'
                      : ''
                  "
                  @click="$emit('open-penalidad', item)"
                >
                  {{ messages.EDIT_PENALIDAD_BUTTON }}
                </b-button>
              </div>
              <span v-else class="text-muted">—</span>
            </template>

            <template #cell(obsProveedor)="{ value }">
              <span class="gc-col-wrap">{{ value || "-" }}</span>
            </template>

            <template #cell(obsGestor)="{ item }">
              <span v-if="item.esAdicional" class="text-muted">—</span>
              <b-form-textarea
                v-else-if="editable && item._observando"
                v-model="item._obsTmp"
                rows="3"
                class="grey-input"
                placeholder="Escriba el motivo de la observación"
                @input="$emit('mark-observada', item)"
              />
              <span v-else class="gc-col-wrap">{{
                item.obsGestor || "-"
              }}</span>
            </template>

            <template #cell(estado)="{ item, value }">
              <b-badge
                v-if="!item.esAdicional"
                :variant="estadoRowBadge(value)"
              >
                {{ value || "-" }}
              </b-badge>
              <span v-else class="text-muted">—</span>
            </template>

            <template #cell(confirmar)="{ item }">
              <div
                class="d-flex align-items-center justify-content-center gap-1"
              >
                <b-button
                  v-if="!item.esAdicional && editable && showConfirmRow(item)"
                  size="sm"
                  variant="success"
                  v-b-tooltip.hover
                  title="Confirmar subsanación"
                  @click="$emit('confirm-row', item)"
                >
                  ✓
                </b-button>

                <b-button
                  v-if="
                    !item.esAdicional &&
                      editable &&
                      puedeIniciarObservacion(item) &&
                      !item._observando
                  "
                  size="sm"
                  variant="outline-warning"
                  v-b-tooltip.hover
                  title="Observar OS"
                  @click="$emit('start-observar', item)"
                >
                  <font-awesome-icon icon="exclamation-triangle" />
                </b-button>

                
                <b-button
                  v-if="!item.esAdicional && canAddAdicional(item)"
                  size="sm"
                  variant="outline-success"
                  v-b-tooltip.hover
                  title="Agregar adicional"
                  @click="$emit('open-adicional', item)"
                >
                  <font-awesome-icon icon="plus" />
                </b-button>

   
                <b-button
                  v-if="item.esAdicional && canDeleteAdicional(item)"
                  size="sm"
                  variant="outline-danger"
                  v-b-tooltip.hover
                  title="Eliminar adicional"
                  @click="$emit('delete-adicional', item)"
                >
                  <font-awesome-icon icon="trash" />
                </b-button>
              </div>
            </template>

            <template #cell(revertir)="{ item }">
              <b-button
                v-if="!item.esAdicional && editable && item._dirty"
                size="sm"
                variant="outline-secondary"
                v-b-tooltip.hover
                title="Revertir"
                @click="$emit('revert-row', item)"
              >
                ↺
              </b-button>
            </template>
          </b-table>
        </b-col>
      </b-row>

      <!--<b-row
        v-if="editable && items.length"
        class="gestor-centralizado-total-bar-row"
      >
        <b-col cols="12" class="text-right">
          <div class="gestor-centralizado-total-bar">
            <span class="gestor-centralizado-total-bar__label">
              {{ messages.TOTAL_FACTURAR_LABEL }}
            </span>
            <span class="gestor-centralizado-total-bar__value">
              {{ formatMoney(totalAFacturar) }}
            </span>
          </div>
        </b-col>
      </b-row> -->

      <b-row v-if="showPagination && resolvedTotalRows > perPage">
        <b-col cols="12">
          <b-pagination
            :value="currentPage"
            :total-rows="resolvedTotalRows"
            :per-page="perPage"
            pills
            align="center"
            size="md"
            class="my-3 gestor-centralizado-pagination"
            @input="$emit('update:currentPage', $event)"
          />
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import {
  formatMoney,
  formatPenalidadTooltip,
} from "@/services/gestor-centralizado/gestor-centralizado.formatters";
import {
  computeTotalAFacturar,
  hasPenalidadDisplay,
  porcentajePenalidadRow,
  tooltipPenalidadesRow,
  totalPenalidadRow,
} from "@/services/gestor-centralizado/gestor-centralizado.calculations";
import {
  getGestorCentralizadoEditableTableFields,
  getGestorCentralizadoTableFields,
  MESSAGES,
  MONEY_COLUMNS,
} from "@/services/gestor-centralizado/gestor-centralizado.constants";
import {
  canEditCostoGestor,
  canEditCantidadGestor,
  canEditPenalidadesGestor,
  requiresObsConfirmacion,
  showConfirmRow as shouldShowConfirmRow,
} from "@/services/gestor-centralizado/gestor-centralizado.rules";

export default {
  name: "GestorCentralizadoTable",

  props: {
    items: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    loadError: { type: String, default: "" },
    editable: { type: Boolean, default: true },
    estadoLiquidacion: { type: String, default: "" },
    idLiquidacionCab: { type: [Number, String], default: null },
    sectionTitle: { type: String, default: MESSAGES.RESULTS_TITLE },
    emptyText: { type: String, default: MESSAGES.EMPTY_TABLE },
    loadingText: { type: String, default: MESSAGES.LOADING },
    currentPage: { type: Number, default: 1 },
    perPage: { type: Number, default: 10 },
    totalRows: { type: Number, default: 0 },
    showPagination: { type: Boolean, default: true },
    showHeaderInfo: { type: Boolean, default: true },
  },

  data() {
    return {
      messages: MESSAGES,
    };
  },

  computed: {
    resolvedTotalRows() {
      return this.totalRows > 0 ? this.totalRows : this.items.length;
    },

    canEditPenalidades() {
      return canEditPenalidadesGestor(this.estadoLiquidacion);
    },
    canEditCantidad() {
      return canEditCantidadGestor(this.estadoLiquidacion);
    },
    totalAFacturar() {
      return computeTotalAFacturar(this.items); // ← sin filtrar, ya no excluye adicionales
    },

    tableFields() {
      const moneySet = new Set(MONEY_COLUMNS);
      const fields = this.editable
        ? getGestorCentralizadoEditableTableFields()
        : getGestorCentralizadoTableFields();

      return fields.map((field) => {
        if (field.key === "costoTotalSinIgv" || field.key === "penalidad")
          return field;
        if (!moneySet.has(field.key)) return field;
        return { ...field, formatter: (value) => formatMoney(value) };
      });
    },
  },

  methods: {
    isParentObservando(adicional) {
      const padre = this.items.find(
        (i) =>
          !i.esAdicional && i.detSequenceId === adicional._parentDetSequenceId,
      );
      return padre?._observando === true;
    },
    puedeIniciarObservacion(item) {
      return !["OBSERVADA", "ACEPTADA", "LIQUIDACION_RESUELTA"].includes(
        item.estado,
      );
    },
    canAddAdicional(item) {
      return (
        this.editable &&
        this.estadoLiquidacion === "PRE_LIQUIDACION" &&
        item.estado !== "OBSERVADA" &&
        !item._observando
      );
    },
    canDeleteAdicional(item) {
      if (!this.editable || this.estadoLiquidacion !== "PRE_LIQUIDACION") return false;
      const parent = this.items.find(
        (p) => !p.esAdicional && p.detSequenceId === item._parentDetSequenceId
      );
      if (parent && (parent.estado === "OBSERVADA" || parent._observando)) return false;
      return true;
    },
    formatMoney,
    totalPenalidad: totalPenalidadRow,
    porcentajePenalidad: porcentajePenalidadRow,
    tooltipPenalidades: tooltipPenalidadesRow,

    showPenalidadValue(item) {
      return hasPenalidadDisplay(item);
    },

    penalidadTooltip(item) {
      return formatPenalidadTooltip(item);
    },

    penalidadTooltipClass(item) {
      return formatPenalidadTooltip(item)
        ? "gestor-centralizado-penalidad-tooltip"
        : "";
    },

    formatPenalidadDisplay(item) {
      const amount = totalPenalidadRow(item);
      if (amount > 0) {
        const porcentaje = porcentajePenalidadRow(item);
        const suffix = porcentaje > 0 ? ` (${porcentaje}%)` : "";
        return `- S/ ${amount.toFixed(2)}${suffix}`;
      }
      return formatMoney(item.penalidad);
    },

    canEditCosto(item) {
      return canEditCostoGestor(this.estadoLiquidacion, item);
    },

    requiresObsConfirmacion(item) {
      return requiresObsConfirmacion(this.estadoLiquidacion, item);
    },

    showConfirmRow(item) {
      return shouldShowConfirmRow(this.estadoLiquidacion, item);
    },

    rowClass(item) {
      if (!item) return "";
      const classes = [];
      if (item.esAdicional) classes.push("table-success");
      if (item.esManual) classes.push("gc-row-manual");
      return classes.join(" ");
    },

    estadoRowBadge(estado) {
      return (
        {
          PRE_LIQUIDACION: "primary",
          OBSERVADA: "warning",
          PENDIENTE_REVISION: "secondary",
          ACEPTADA: "success",
          LIQUIDACION_RESUELTA: "success",
          LIQUIDADA: "dark",
        }[estado] || "secondary"
      );
    },
  },
};
</script>
